import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../config/network";
import { useAuth } from "./AuthContext";

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { token } = useAuth();
  const socketRef = useRef(null);
  const [socketInstance, setSocketInstance] = useState(null);
  const [connected, setConnected] = useState(false);
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      return;
    }

    const socket = io(SOCKET_URL, {
      auth: { token },
      autoConnect: true,
      transports: ["websocket", "polling"],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5
    });
    socketRef.current = socket;
    setSocketInstance(socket);
    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketInstance(null);
      setConnected(false);
    };
  }, [token]);

  useEffect(() => {
    if (!socketInstance) return undefined;

    const reconnectIfNeeded = () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setConnected(false);
        return;
      }
      if (!socketInstance.connected) {
        socketInstance.connect();
      }
    };

    const onOffline = () => {
      setIsOnline(false);
      setConnected(false);
    };

    const onOnline = () => {
      setIsOnline(true);
      reconnectIfNeeded();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        reconnectIfNeeded();
      }
    };

    const onResume = () => {
      reconnectIfNeeded();
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("focus", reconnectIfNeeded);
    document.addEventListener("visibilitychange", onVisibilityChange);
    document.addEventListener("resume", onResume);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("focus", reconnectIfNeeded);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("resume", onResume);
    };
  }, [socketInstance]);

  const value = useMemo(() => ({ socket: socketInstance, connected, isOnline }), [socketInstance, connected, isOnline]);
  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) throw new Error("useSocket must be used inside SocketProvider");
  return context;
}
